import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import "../../scss/main.scss";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { RHAP_UI } from "react-h5-audio-player";
import {
    fetchLatestArticleMeta,
    fetchArticleAudioBlob,
} from "../../api/audioapi";
import { useI18n } from "../../i18n/I18nProvider";

const HollowGlobe = lazy(() => import("./HollowGlobe"));

const CDN24_LOGO_PL = process.env.PUBLIC_URL + "./cdn24info.png";
const CDN24_LOGO_ENG = process.env.PUBLIC_URL + "./cdn24.info ang.png";
const CDN24_URL = "https://cdn24.info";

type IdleWindow = Window & {
    __cyberNavTypewritersDone?: boolean;
    requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
};

const MainComponent: React.FC = () => {
    const { t, language } = useI18n();
    const [newsTitle, setNewsTitle] = useState(t("main_loading"));
    const [audioSrc, setAudioSrc] = useState<string | null>(null);
    const [audioDesc, setAudioDesc] = useState<string>("");
    const [articleSource, setArticleSource] = useState<string>("");
    const audioRef = useRef<AudioPlayer | null>(null);
    const globeSectionRef = useRef<HTMLElement | null>(null);

    const [showGlobe, setShowGlobe] = useState(false);
    const [isGlobeEngaged, setIsGlobeEngaged] = useState(false);
    useEffect(() => {
        const target = globeSectionRef.current;
        if (!target) return;
        const idleWindow = window as IdleWindow;
        const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

        let cancelled = false;
        let idleId: number | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let navReadyCleanup: (() => void) | null = null;
        let pageLoadCleanup: (() => void) | null = null;

        const waitForPageLoad = (callback: () => void) => {
            if (document.readyState === "complete") {
                callback();
                return;
            }

            const handleLoad = () => callback();
            window.addEventListener("load", handleLoad, { once: true });
            pageLoadCleanup = () => window.removeEventListener("load", handleLoad);
        };

        const waitForNavAnimations = (callback: () => void) => {
            if (!isDesktop || idleWindow.__cyberNavTypewritersDone) {
                callback();
                return;
            }

            const handleDone = () => callback();
            window.addEventListener("nav-typewriters-done", handleDone, {
                once: true,
            });
            navReadyCleanup = () =>
                window.removeEventListener("nav-typewriters-done", handleDone);
        };

        const scheduleLoad = () => {
            if (cancelled || showGlobe) return;

            const startLoading = () => {
                if (!cancelled) {
                    setShowGlobe(true);
                }
            };

            const queueIdleLoad = () => {
                const delayedStart = () => {
                    if (cancelled) return;

                    if (typeof idleWindow.requestIdleCallback === "function") {
                        idleId = idleWindow.requestIdleCallback(startLoading, {
                            timeout: isDesktop ? 3200 : 1200,
                        });
                        return;
                    }

                    timeoutId = setTimeout(startLoading, isDesktop ? 2200 : 700);
                };

                timeoutId = setTimeout(delayedStart, isDesktop ? 2600 : 400);
            };

            waitForPageLoad(() => {
                if (cancelled) return;
                waitForNavAnimations(() => {
                    if (cancelled) return;
                    queueIdleLoad();
                });
            });
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    scheduleLoad();
                    observer.disconnect();
                }
            },
            {
                rootMargin: "200px 0px",
                threshold: 0.05,
            }
        );

        observer.observe(target);

        return () => {
            cancelled = true;
            observer.disconnect();
            navReadyCleanup?.();
            pageLoadCleanup?.();
            if (idleId !== null && typeof idleWindow.cancelIdleCallback === "function") {
                idleWindow.cancelIdleCallback(idleId);
            }
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
        };
    }, [showGlobe]);

    const cdn24Logo = language === "pl" ? CDN24_LOGO_PL : CDN24_LOGO_ENG;

    useEffect(() => {
        let isMounted = true;
        let objectUrl: string | null = null;

        setAudioSrc(null);
        setNewsTitle(t("main_loading"));
        setAudioDesc("");
        setArticleSource("");

        const currentLang = language;

        const load = async () => {
            try {
                const meta = await fetchLatestArticleMeta(currentLang);
                if (!isMounted) return;

                setNewsTitle(meta.title);
                setAudioDesc(meta.summary);
                setArticleSource(meta.source);

                console.log(
                    `[Audio] Fetching audio for articleId=${meta.articleId}, language="${currentLang}"`
                );
                const audioBlob = await fetchArticleAudioBlob(
                    meta.articleId,
                    currentLang
                );
                if (!isMounted) return;
                console.log(
                    `[Audio] Got blob size=${audioBlob.size}, type=${audioBlob.type}`
                );

                objectUrl = URL.createObjectURL(audioBlob);
                setAudioSrc(objectUrl);
            } catch (err) {
                console.error("Błąd ładowania artykułu/audio:", err);
                if (!isMounted) return;

                setNewsTitle(t("main_fallback_title"));
                setAudioDesc(t("main_fallback_desc"));
                setArticleSource(t("main_fallback_source"));
                setAudioSrc(
                    "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
                );
            }
        };

        load();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [language, t]);

    const handleAudioKeyDownCapture = (
        e: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

        const player = audioRef.current;
        const audioEl = player?.audio?.current;
        if (!audioEl) return;

        e.preventDefault();
        e.stopPropagation();

        const STEP = 0.05;
        const current = Number.isFinite(audioEl.volume) ? audioEl.volume : 1;

        let next =
            e.key === "ArrowUp"
                ? Math.min(1, current + STEP)
                : Math.max(0, current - STEP);

        if (!Number.isFinite(next)) {
            next = current;
        }

        audioEl.volume = next;
    };

    const skipTime = (seconds: number) => {
        const audioEl = audioRef.current?.audio?.current;
        if (!audioEl) return;
        audioEl.currentTime = Math.min(
            Math.max(0, audioEl.currentTime + seconds),
            audioEl.duration || Infinity
        );
    };

    return (
        <main className="main">
            <section
                className="cyber-main-layout"
                aria-label={t("main_section_label")}
            >
                <article className="cyber-audio-section" data-aos="fade-right">
                    <div className="cyber-audio-glow"></div>

                    <div className="cyber-glass-panel">
                        <header className="cyber-glass-header">
                            <h2 className="cyber-glass-title">
                                <a
                                    href={CDN24_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cyber-glass-title-link"
                                >
                                    {newsTitle.length > 80
                                        ? newsTitle.slice(0, 80) + "…"
                                        : newsTitle}
                                </a>
                            </h2>

                            <div className="cyber-pulse-group">
                                <div className="pulse-bar pulse-1"></div>
                                <div className="pulse-bar pulse-2"></div>
                                <div className="pulse-bar pulse-3"></div>
                                <div className="pulse-bar pulse-4"></div>
                            </div>
                        </header>

                        <p className="cyber-glass-desc">{audioDesc || ""}</p>

                        {articleSource && (
                            <div className="cyber-glass-source-row">
                                <p className="cyber-glass-source">
                                    {t("main_source_prefix")}
                                    <strong>{articleSource}</strong>
                                </p>
                                <p className="cyber-glass-cdn">
                                    {t("main_cdn_note")}
                                    <a
                                        href={CDN24_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        cdn24.info
                                    </a>
                                </p>
                            </div>
                        )}

                        <div
                            className="cyber-audio-player-wrapper"
                            onKeyDownCapture={handleAudioKeyDownCapture}
                        >
                            <AudioPlayer
                                ref={audioRef}
                                src={audioSrc ?? undefined}
                                showJumpControls={false}
                                layout="stacked"
                                customAdditionalControls={[]}
                                customProgressBarSection={[
                                    RHAP_UI.CURRENT_TIME,
                                    RHAP_UI.PROGRESS_BAR,
                                    RHAP_UI.DURATION,
                                ]}
                                customControlsSection={[
                                    <button
                                        key="back5"
                                        type="button"
                                        className="cyber-skip-btn"
                                        aria-label={t("main_skip_back")}
                                        onClick={() => skipTime(-5)}
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="1 4 1 10 7 10" />
                                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                        </svg>
                                    </button>,
                                    RHAP_UI.MAIN_CONTROLS,
                                    <button
                                        key="fwd15"
                                        type="button"
                                        className="cyber-skip-btn"
                                        aria-label={t("main_skip_forward")}
                                        onClick={() => skipTime(15)}
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="23 4 23 10 17 10" />
                                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                        </svg>
                                    </button>,
                                    RHAP_UI.VOLUME_CONTROLS,
                                ]}
                                autoPlay={false}
                            />
                        </div>
                    </div>
                </article>

                <aside
                    className="cyber-globe-section"
                    data-aos="fade-left"
                    ref={globeSectionRef}
                >
                    <a
                        href={CDN24_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="globe-link"
                        aria-label={t("main_cdn24_label")}
                        onMouseEnter={() => setIsGlobeEngaged(true)}
                        onMouseLeave={() => setIsGlobeEngaged(false)}
                        onFocus={() => setIsGlobeEngaged(true)}
                        onBlur={() => setIsGlobeEngaged(false)}
                    >
                        <div className="globe-wrapper">
                            {showGlobe && (
                                <Suspense fallback={null}>
                                    <HollowGlobe active={isGlobeEngaged} />
                                </Suspense>
                            )}
                        </div>
                        <div className="globe-logo-overlay">
                            <img
                                src={cdn24Logo}
                                alt={t("main_cdn24_alt")}
                                className="globe-cdn24-logo"
                                draggable={false}
                            />
                        </div>
                    </a>
                </aside>
            </section>
        </main>
    );
};

export default MainComponent;
